import { dateUtils } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'

import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import type { TemplateVersionInfo } from '../../types/templateModel'
import { NO_DATE_SYMBOL } from '../../util/constants'
import { fromDateAsLocalDate } from '../../util/versionDateUtils'
import { useOperationsContext } from '../../workspace/operationsContext'
import { ARROW_LINE_WIDTH } from '../versionedTimeline/constants'
import { OperationVersionCircle } from '../versionedTimeline/OperationVersionCircle'
import type { useTimelineScale } from '../versionedTimeline/useTimelineScale'
import { TemplateVersionTooltipContent } from './TemplateVersionTooltipContent'
import { useIsTemplateVersionActive } from './useIsTemplateVersionActive'

type Props = {
  version: TemplateVersionInfo
  dateToPixel: ReturnType<typeof useTimelineScale>['dateToPixel']
  isInherited: (version: TemplateVersionInfo) => boolean
  currentVersionLineId: string
  earliestVersionDate: Date
}

export const SingleTemplateVersionCircle = ({
  version,
  dateToPixel,
  isInherited,
  currentVersionLineId,
  earliestVersionDate,
}: Props) => {
  const { workspaceKey, branchKey } = useOperationsContext()
  const { server } = useNavigationParams()
  const checkIsVersionActive = useIsTemplateVersionActive(currentVersionLineId)
  const isActive = checkIsVersionActive(version)
  const cxPosition = dateToPixel(
    version.fromDate
      ? dateUtils.dateUtcAsLocal(version.fromDate).toDate()
      : NO_DATE_SYMBOL,
  )

  const fromDate = version.fromDate
  const fromDateAsDate = fromDateAsLocalDate(fromDate)

  return (
    <Tooltip
      tip={
        <TemplateVersionTooltipContent
          versions={[version]}
          versionId={currentVersionLineId}
          earliestVersionDate={earliestVersionDate}
        />
      }
      isInteractive
    >
      <g>
        <OperationVersionCircle
          isActive={isActive}
          isDraft={version.isDraft}
          isInherited={isInherited(version)}
          isHideVersion={version.isHideVersion}
          cy={ARROW_LINE_WIDTH / 2}
          cx={cxPosition}
          to={{
            path: configurationItem({
              layer: version.layer,
              block: version.block,
              itemId: version.identifier,
              type: 'template',
              workspaceName: workspaceKey?.name,
              branch: branchKey?.name,
              server: workspaceKey?.branchKey.server ?? server,
              untilChangesetKey: version.changesetHistory?.changesetKey,
            }),
            atTime: fromDateAsDate ?? earliestVersionDate,
          }}
          isBeginningOfTime={!version.fromDate}
        />
      </g>
    </Tooltip>
  )
}
